import React, { useState, useEffect } from 'react';
import { formatCost, formatDuration } from '../../../../services/callService';
import { formatMemoryDate } from '../../../../services/memoryService';

const StatsCards = ({ callStats, memoryStats, onRefresh, viewMode = 'admin' }) => {
  const [animatedValues, setAnimatedValues] = useState({});

  // Animate numbers on load
  useEffect(() => {
    if (!callStats && !memoryStats) return;

    const targets = {
      totalCalls: callStats?.total_calls || 0,
      totalMemories: memoryStats?.total_memories || 0,
      successRate: callStats?.success_rate || 0,
      totalCost: callStats?.total_cost || 0
    };

    // Simple animation by stepping towards target values
    const animate = (key, target, duration = 1000) => {
      const start = animatedValues[key] || 0;
      const startTime = Date.now();
      
      const updateValue = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3); // Ease out cubic
        const current = start + (target - start) * easeOut;
        
        setAnimatedValues(prev => ({ ...prev, [key]: current }));
        
        if (progress < 1) {
          requestAnimationFrame(updateValue);
        }
      };
      
      requestAnimationFrame(updateValue);
    };

    Object.entries(targets).forEach(([key, target]) => {
      animate(key, target);
    });
  }, [callStats, memoryStats]);

  // Calculate trends and insights
  const getTrend = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change).toFixed(1),
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
      isPositive: change > 0
    };
  };

  // Stats card configuration
  const statsConfig = [
    // Row 1: Primary Call Metrics
    {
      id: 'total-calls',
      title: 'Total Contacts',
      value: Math.round(animatedValues.totalCalls || 0),
      subtitle: callStats?.first_call_date 
        ? `Since ${new Date(callStats.first_call_date).toLocaleDateString()}`
        : 'No contacts yet',
      icon: '📞',
      color: 'blue',
      trend: getTrend(callStats?.calls_this_month, callStats?.calls_this_month - (callStats?.calls_this_week || 0))
    },
    {
      id: 'success-rate',
      title: 'Success Rate',
      value: `${Math.round(animatedValues.successRate || 0)}%`,
      subtitle: callStats?.total_calls > 0
        ? `${callStats.total_calls} total contacts`
        : 'No contacts to analyze',
      icon: '✅',
      color: callStats?.success_rate >= 80 ? 'green' : callStats?.success_rate >= 60 ? 'yellow' : 'red',
      showProgress: true,
      progressValue: callStats?.success_rate || 0
    },
    {
      id: 'avg-duration',
      title: 'Avg Contact Duration',
      value: formatDuration(callStats?.avg_duration_minutes),
      subtitle: `Total: ${formatDuration(callStats?.total_duration_minutes)}`,
      icon: '⏱️',
      color: 'purple',
      trend: getTrend(callStats?.avg_duration_minutes, 10) // Compare to 10min baseline
    },
    {
      id: 'total-cost',
      title: 'Total Cost',
      value: formatCost(animatedValues.totalCost || 0),
      subtitle: `Avg: ${formatCost(callStats?.avg_cost)}`,
      icon: '💰',
      color: 'orange'
    },
    
    // Row 2: Memory & Engagement Metrics
    {
      id: 'total-memories',
      title: 'Total Memories',
      value: Math.round(animatedValues.totalMemories || 0),
      subtitle: memoryStats?.latest_memory_date 
        ? `Latest: ${formatMemoryDate(memoryStats.latest_memory_date)}`
        : 'No memories yet',
      icon: '🧠',
      color: 'indigo',
      trend: getTrend(memoryStats?.memories_this_month, memoryStats?.memories_this_month - (memoryStats?.memories_this_week || 0))
    },
    {
      id: 'categories-used',
      title: 'Categories Used',
      value: memoryStats?.categories_used || 0,
      subtitle: `of 7 available`,
      icon: '🏷️',
      color: 'pink',
      showProgress: true,
      progressValue: ((memoryStats?.categories_used || 0) / 7) * 100
    },
    {
      id: 'recent-activity',
      title: 'This Week',
      value: `${callStats?.calls_this_week || 0} contacts`,
      subtitle: `${memoryStats?.memories_this_week || 0} memories`,
      icon: '📈',
      color: 'green'
    },
    {
      id: 'performance',
      title: 'Performance',
      value: `${Math.round(callStats?.avg_model_latency || 0)}ms`,
      subtitle: `Avg latency`,
      icon: '⚡',
      color: (callStats?.avg_model_latency || 0) < 1000 ? 'green' : 
             (callStats?.avg_model_latency || 0) < 2000 ? 'yellow' : 'red'
    }
  ];

  // In client mode show only: Total Contacts, Contacts This Week, Total Memories, Avg Duration
  const clientTileIds = ['total-calls', 'recent-activity', 'total-memories', 'avg-duration'];
  const visibleStats = viewMode === 'client'
    ? clientTileIds.map(id => statsConfig.find(s => s.id === id)).filter(Boolean)
    : statsConfig;

  // Color mapping for different states
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-900',
      icon: 'text-blue-600'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-900',
      icon: 'text-green-600'
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-900',
      icon: 'text-yellow-600'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-900',
      icon: 'text-red-600'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-900',
      icon: 'text-purple-600'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-900',
      icon: 'text-orange-600'
    },
    indigo: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      text: 'text-indigo-900',
      icon: 'text-indigo-600'
    },
    pink: {
      bg: 'bg-pink-50',
      border: 'border-pink-200',
      text: 'text-pink-900',
      icon: 'text-pink-600'
    }
  };

  // Render trend indicator
  const TrendIndicator = ({ trend }) => {
    if (!trend) return null;

    return (
      <div className={`flex items-center text-xs ${
        trend.direction === 'up' ? 'text-green-600' : 
        trend.direction === 'down' ? 'text-red-600' : 'text-gray-600'
      }`}>
        {trend.direction === 'up' && (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        )}
        {trend.direction === 'down' && (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
        {trend.value}%
      </div>
    );
  };

  // Render progress bar
  const ProgressBar = ({ value, color }) => {
    const progressColor = colorClasses[color]?.icon || 'text-blue-600';
    
    return (
      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
        <div 
          className={`h-2 rounded-full transition-all duration-1000 ease-out`}
          style={{ 
            width: `${Math.min(value, 100)}%`,
            backgroundColor: `var(--${color}-500, #3B82F6)`
          }}
        />
      </div>
    );
  };

  if (!callStats && !memoryStats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow border animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="w-8 h-8 bg-gray-200 rounded"></div>
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Performance Overview</h2>
        <button
          onClick={onRefresh}
          className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {visibleStats.map((stat) => {
          const colors = colorClasses[stat.color] || colorClasses.blue;
          
          return (
            <div
              key={stat.id}
              className={`relative p-6 rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md ${colors.bg} ${colors.border}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className={`text-2xl ${colors.icon}`}>
                  {stat.icon}
                </div>
                <TrendIndicator trend={stat.trend} />
              </div>

              {/* Main Value */}
              <div className={`text-2xl font-bold ${colors.text} mb-1 truncate`}>
                {stat.value}
              </div>

              {/* Subtitle */}
              <div className="text-sm text-gray-600 mb-2">
                {stat.subtitle}
              </div>

              {/* Title */}
              <div className={`text-xs font-medium ${colors.text} uppercase tracking-wide`}>
                {stat.title}
              </div>

              {/* Progress Bar */}
              {stat.showProgress && (
                <ProgressBar value={stat.progressValue} color={stat.color} />
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Insights — hidden in client mode */}
      {viewMode !== 'client' && (callStats?.total_calls > 0 || memoryStats?.total_memories > 0) && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Quick Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              {callStats?.total_calls > 0 ? (
                <ul className="space-y-1">
                  <li>• Average contact lasts {formatDuration(callStats.avg_duration_minutes)}</li>
                  <li>• {callStats.calls_this_week} contacts this week</li>
                  {callStats.success_rate >= 80 && <li>• Excellent success rate! 🎉</li>}
                  {callStats.success_rate < 50 && <li>• Success rate needs improvement</li>}
                </ul>
              ) : (
                <p>No contact data available yet</p>
              )}
            </div>
            <div>
              {memoryStats?.total_memories > 0 ? (
                <ul className="space-y-1">
                  <li>• {memoryStats.memories_this_week} memories added this week</li>
                  <li>• Using {memoryStats.categories_used} of 7 categories</li>
                  {memoryStats.avg_memories_per_week >= 2 && <li>• Consistent memory creation! 👍</li>}
                  {memoryStats.categories_used === 7 && <li>• All categories explored! 🌟</li>}
                </ul>
              ) : (
                <p>No memories created yet</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsCards;